import { format, isValid, parseISO, parse } from 'date-fns';
import { is } from './is';

/**
 * Converts UTC timestamp to local timezone and formats it
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @param {string} formatString - Date format string (default: "dd MMM yyyy hh:mm:ss a")
 * @returns {string} Formatted local time string
 */
export const convertUTCToLocal = (utcTimestamp, formatString = "dd MMM yyyy hh:mm:ss a") => {
  if (is.empty(utcTimestamp)) return "";
  
  try {
    // Parse the UTC timestamp
    const utcDate = typeof utcTimestamp === 'string' ? parseISO(utcTimestamp) : utcTimestamp;
    
    // Check if the date is valid
    if (!isValid(utcDate)) {
      console.warn('Invalid UTC timestamp:', utcTimestamp);
      return "";
    }
    
    // Format the date in local timezone
    return format(utcDate, formatString);
  } catch (error) {
    console.error('Error converting UTC to local time:', error);
    return "";
  }
};

/**
 * Converts UTC timestamp to local timezone and returns only time
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @returns {string} Formatted local time string (hh:mm:ss a)
 */
export const convertUTCToLocalTime = (utcTimestamp) => {
  return convertUTCToLocal(utcTimestamp, "hh:mm:ss a");
};

/**
 * Converts UTC timestamp to local timezone and returns only date
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @returns {string} Formatted local date string (dd MMM yyyy)
 */
export const convertUTCToLocalDate = (utcTimestamp) => {
  return convertUTCToLocal(utcTimestamp, "dd MMM yyyy");
};

/**
 * Converts UTC timestamp to local timezone and returns date and time
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @returns {string} Formatted local date and time string (dd MMM yyyy hh:mm:ss a)
 */
export const convertUTCToLocalDateTime = (utcTimestamp) => {
  return convertUTCToLocal(utcTimestamp, "dd MMM yyyy hh:mm:ss a");
};

/**
 * Converts UTC timestamp to local timezone and returns relative time (e.g., "2 hours ago")
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @returns {string} Relative time string
 */
export const convertUTCToRelativeTime = (utcTimestamp) => {
  if (is.empty(utcTimestamp)) return "";
  
  try {
    const utcDate = typeof utcTimestamp === 'string' ? parseISO(utcTimestamp) : utcTimestamp;
    
    if (!isValid(utcDate)) {
      console.warn('Invalid UTC timestamp:', utcTimestamp);
      return "";
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - utcDate) / 1000);
    
    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return format(utcDate, "dd MMM yyyy");
  } catch (error) {
    console.error('Error converting UTC to relative time:', error);
    return "";
  }
};

/**
 * Gets current local time in formatted string
 * @param {string} formatString - Date format string (default: "dd MMM yyyy hh:mm:ss a")
 * @returns {string} Current local time formatted string
 */
export const getCurrentLocalTime = (formatString = "dd MMM yyyy hh:mm:ss a") => {
  return format(new Date(), formatString);
};

/**
 * Converts multiple UTC timestamps in an object to local timezone
 * @param {Object} data - Object containing UTC timestamps
 * @param {Array} timestampFields - Array of field names that contain UTC timestamps
 * @param {string} formatString - Date format string (default: "dd MMM yyyy hh:mm:ss a")
 * @returns {Object} Object with converted timestamps
 */
export const convertMultipleUTCToLocal = (data, timestampFields, formatString = "dd MMM yyyy hh:mm:ss a") => {
  if (!data || typeof data !== 'object') return data;
  
  const convertedData = { ...data };
  
  timestampFields.forEach(field => {
    if (convertedData[field]) {
      convertedData[field] = convertUTCToLocal(convertedData[field], formatString);
    }
  });
  
  return convertedData;
};

/**
 * Converts local time to UTC timestamp for API payloads
 * @param {string|Date} localTime - Local time string or Date object
 * @param {string} inputFormat - Format of input string (if string provided)
 * @returns {string} UTC timestamp in ISO format
 */
export const convertLocalToUTC = (localTime, inputFormat = null) => {
  if (is.empty(localTime)) return "";
  
  try {
    let localDate;
    
    if (typeof localTime === 'string') {
      if (inputFormat) {
        // Parse using specific format
        localDate = parse(localTime, inputFormat, new Date());
      } else {
        // Try to parse as ISO or common formats
        localDate = new Date(localTime);
      }
    } else {
      localDate = localTime;
    }
    
    if (!isValid(localDate)) {
      console.warn('Invalid local time:', localTime);
      return "";
    }
    
    // Convert to UTC ISO string
    return localDate.toISOString();
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    return "";
  }
};

/**
 * Converts multiple local timestamps in an object to UTC for API payloads
 * @param {Object} data - Object containing local timestamps
 * @param {Array} timestampFields - Array of field names that contain local timestamps
 * @param {string} inputFormat - Format of input strings (if strings provided)
 * @returns {Object} Object with converted UTC timestamps
 */
export const convertMultipleLocalToUTC = (data, timestampFields, inputFormat = null) => {
  if (!data || typeof data !== 'object') return data;
  
  const convertedData = { ...data };
  
  timestampFields.forEach(field => {
    if (convertedData[field]) {
      convertedData[field] = convertLocalToUTC(convertedData[field], inputFormat);
    }
  });
  
  return convertedData;
};

/**
 * Prepares API payload by converting local timestamps to UTC
 * @param {Object} payload - API payload object
 * @param {Array} timestampFields - Fields that need UTC conversion
 * @returns {Object} Payload with UTC timestamps
 */
export const prepareApiPayload = (payload, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  if (!payload || typeof payload !== 'object') return payload;
  
  return convertMultipleLocalToUTC(payload, timestampFields);
};

/**
 * Converts UTC timestamp to local timezone with domain-specific handling
 * @param {string|Date} utcTimestamp - UTC timestamp from backend API
 * @param {string} formatString - Date format string (default: "dd MMM yyyy hh:mm:ss a")
 * @param {string} domain - Domain name for timezone-specific handling
 * @returns {string} Formatted local time string
 */
export const convertUTCToLocalWithDomain = (utcTimestamp, formatString = "dd MMM yyyy hh:mm:ss a", domain = null) => {
  if (is.empty(utcTimestamp)) {
    // console.log('[Timezone] Empty timestamp provided for conversion');
    return "";
  }
  
  try {
    // console.log(`[Timezone] Converting UTC to local - Input: ${utcTimestamp}, Domain: ${domain}, Format: ${formatString}`);
    
    let utcDate;
    
    // Handle different timestamp formats
    if (typeof utcTimestamp === 'string') {
      // Check if it's in the format "2025-10-21 10:59:06.584000"
      if (utcTimestamp.includes(' ') && utcTimestamp.includes('.')) {
        // Convert to ISO format by replacing space with T and handling microseconds
        const isoString = utcTimestamp.replace(' ', 'T');
        utcDate = new Date(isoString);
        // console.log(`[Timezone] Converted custom format to ISO: ${isoString}`);
      } else {
        // Try standard parseISO for other formats
        utcDate = parseISO(utcTimestamp);
      }
    } else {
      utcDate = utcTimestamp;
    }
    
    // Check if the date is valid
    if (!isValid(utcDate)) {
      // console.warn('[Timezone] Invalid UTC timestamp:', utcTimestamp);
      return "";
    }
    
    // Log the UTC date for debugging
    // console.log(`[Timezone] Parsed UTC date: ${utcDate.toISOString()}`);
    // console.log(`[Timezone] Browser timezone offset: ${utcDate.getTimezoneOffset()} minutes`);
    
    // For invoice display, always show local time regardless of domain
    // The browser's timezone will be used automatically by date-fns format
    const localTime = format(utcDate, formatString);
    // console.log(`[Timezone] Converted to local time: ${localTime}`);
    
    return localTime;
  } catch (error) {
    console.error('[Timezone] Error converting UTC to local time:', error);
    return "";
  }
};

/**
 * Converts invoice timestamp format to local timezone
 * Handles the specific format used in invoice API responses
 * @param {string} invoiceTimestamp - Timestamp in format "2025-10-21 10:59:06.584000"
 * @param {string} formatString - Date format string (default: "dd MMM yyyy hh:mm:ss a")
 * @returns {string} Formatted local time string
 */
export const convertInvoiceTimestampToLocal = (invoiceTimestamp, formatString = "dd MMM yyyy hh:mm:ss a") => {
  if (is.empty(invoiceTimestamp)) {
    // console.log('[Invoice Timezone] Empty timestamp provided');
    return "";
  }
  
  try {
    // console.log(`[Invoice Timezone] Converting invoice timestamp: ${invoiceTimestamp}`);
    
    let utcDate;
    
    // Check if timestamp already has timezone info (Z, +, or -)
    // Only consider it has timezone if it's in ISO format with timezone
    const hasTimezone = invoiceTimestamp.includes('Z') || 
                        invoiceTimestamp.includes('+') || 
                        invoiceTimestamp.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}-\d{2}:\d{2}/);
    
    if (hasTimezone) {
      // console.log(`[Invoice Timezone] Timestamp already has timezone info: ${invoiceTimestamp}`);
      utcDate = new Date(invoiceTimestamp);
    } else if (invoiceTimestamp.includes(' ')) {
      // Handle format like "2025-10-21 10:59:42" - treat as UTC
      const [datePart, timePart] = invoiceTimestamp.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes, seconds] = timePart.split(':');
      
      // console.log(`[Invoice Timezone] Parsed components - Year: ${year}, Month: ${month}, Day: ${day}, Hours: ${hours}, Minutes: ${minutes}, Seconds: ${seconds}`);
      
      // Create UTC date object explicitly
      utcDate = new Date(Date.UTC(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds || 0)
      ));
    } else {
      // Standard ISO format or other
      utcDate = new Date(invoiceTimestamp);
    }
    
    // console.log(`[Invoice Timezone] Created UTC date: ${utcDate.toISOString()}`);
    // console.log(`[Invoice Timezone] UTC time: ${utcDate.toUTCString()}`);
    // console.log(`[Invoice Timezone] Local time: ${utcDate.toString()}`);
    
    if (!isValid(utcDate)) {
      console.warn('[Invoice Timezone] Invalid date after conversion:', invoiceTimestamp);
      return "";
    }
    
    // Now the Date object contains the correct UTC time and will display in local timezone
    const localTime = format(utcDate, formatString);
    // console.log(`[Invoice Timezone] Formatted local time: ${localTime}`);
    
    return localTime;
  } catch (error) {
    console.error('[Invoice Timezone] Error converting invoice timestamp:', error);
    return "";
  }
};

/**
 * Common timestamp field names in API responses and requests
 */
export const COMMON_TIMESTAMP_FIELDS = [
  'starttime',
  'stoptime', 
  'createdAt',
  'updatedAt',
  'timestamp',
  'value_timestamp',
  'lastUpdated',
  'startTime',
  'stopTime',
  'transactionTime',
  'chargingStartTime',
  'chargingEndTime',
  'dob', // Date of birth
  'scheduledTime',
  'appointmentTime',
  'bookingTime',
  'checkInTime',
  'checkOutTime'
];